import { useEffect, useRef } from "react";
import { useLocation, useRouteLoaderData } from "react-router";
import { initPostHog, getPostHog } from "./posthog";

export function PostHogProvider({
  posthogKey,
  posthogHost,
  release,
  environment,
  children,
}: {
  posthogKey: string;
  posthogHost: string;
  release?: string;
  environment?: string;
  children: React.ReactNode;
}) {
  const location = useLocation();
  const prevPath = useRef("");

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (posthogKey) {
      initPostHog(posthogKey, posthogHost, { release, environment });
    }
  }, [posthogKey, posthogHost, release, environment]);

  // ── Pageview on route change ──────────────────────────────────────────────
  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === prevPath.current) return;
    prevPath.current = path;
    getPostHog()?.capture("$pageview");
  }, [location.pathname, location.search]);

  // ── Identify user from zone layout loader data ────────────────────────────
  const clientData = useRouteLoaderData("routes/_trade") as
    | { actor?: { id: string; name: string; role: string; authenticated: boolean } }
    | undefined;
  const studioData = useRouteLoaderData("routes/_studio") as
    | { actor?: { id: string; name: string; role: string; authenticated: boolean } }
    | undefined;
  const adminData = useRouteLoaderData("routes/_admin") as
    | { actor?: { id: string; name: string; role: string; authenticated: boolean } }
    | undefined;

  const actor =
    clientData?.actor ?? studioData?.actor ?? adminData?.actor ?? null;

  useEffect(() => {
    if (!actor?.authenticated || !actor.id) return;
    const ph = getPostHog();
    if (!ph) return;

    ph.identify(actor.id, {
      name: actor.name,
      role: actor.role,
      authenticated: true,
    });

    // Group analytics: associate session with the user's business role.
    // Designers and manufacturers are the "B" in B2B2C.
    if (actor.role === "DESIGNER" || actor.role === "MANUFACTURER") {
      ph.group("business", actor.id, {
        name: actor.name,
        role: actor.role,
      });
    }
  }, [actor?.id, actor?.authenticated, actor?.name, actor?.role]);

  // ── Redirect-event capture ────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const event = params.get("_event");
    if (!event) return;

    const edataRaw = params.get("_edata");
    let properties: Record<string, unknown> = {};
    if (edataRaw) {
      try {
        properties = JSON.parse(edataRaw);
      } catch {
        // ignore malformed JSON
      }
    }

    getPostHog()?.capture(event, properties);

    // Dual-emit legacy alias during migration (remove by 2026-04-18)
    const LEGACY_REDIRECT_ALIASES: Record<string, string> = {
      design_created: "design_draft_created",
      design_submitted_for_review: "story_submitted_for_review",
      design_approved: "design_published",
      collection_created: "collection_draft_created",
      collection_submitted_for_review: "collection_story_submitted_for_review",
      collection_approved: "collection_published",
      catalog_listing_submitted: "catalog_submitted",
      catalog_searched: "catalog_search",
    };
    const legacyEvent = LEGACY_REDIRECT_ALIASES[event];
    if (legacyEvent) {
      getPostHog()?.capture(legacyEvent, properties);
    }

    // Clean the URL without triggering a navigation
    params.delete("_event");
    params.delete("_edata");
    const clean =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");
    window.history.replaceState({}, "", clean);
  }, [location.pathname, location.search]);

  return <>{children}</>;
}
